import { useEffect, useState } from "react";
import UserLayout from "../components/custom/layouts/UserLayout";
import { ToastTypes, useToast } from "../components/toast/ToastProvider";
import {
  AppSettings,
  editAppSettings,
  getSettings,
} from "../services/appSetting.service";

const normalizeGroup = (g: string) => g.trim().toLowerCase();
const normalizeKey = (k: string) => k.trim().toLowerCase();

const PRINTER_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "thermal", label: "Thermal" },
  { value: "laser", label: "Laser" },
];

function groupSettingsByGroup(
  list: AppSettings[],
): { groupLabel: string; items: AppSettings[] }[] {
  const map = new Map<string, AppSettings[]>();
  const order: string[] = [];

  for (const s of list) {
    const key = normalizeGroup(s.setting_group);
    if (!map.has(key)) {
      map.set(key, []);
      order.push(key);
    }
    map.get(key)!.push(s);
  }

  return order.map((key) => {
    const items = map.get(key)!;
    return { groupLabel: items[0].setting_group, items };
  });
}

const AppSetting = () => {
  const toast = useToast();

  const [settings, setSettings] = useState<AppSettings[]>([]);
  const [settingValues, setSettingValues] = useState<Record<number, string>>(
    {},
  );

  // Fetch settings from API
  const fetchData = async () => {
    const settingList = await getSettings();
    setSettings(settingList);

    const initialValues: Record<number, string> = {};

    settingList.forEach((s: AppSettings) => {
      if (s.id !== undefined) {
        initialValues[s.id] = s.setting_value;
      }
    });

    setSettingValues(initialValues);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Update one setting
  const handleToggleSetting = (
    settingId: number | undefined,
    newValue: string,
  ) => {
    if (settingId === undefined) return;

    setSettingValues((prev) => ({
      ...prev,
      [settingId]: newValue,
    }));
  };

  // Save all settings
  const handleSaveAll = () => {
    const requests = settings.map((s) => {
      if (s.id === undefined) return Promise.resolve(null);

      return editAppSettings(
        {
          id: s.id,
          setting_group: s.setting_group,
          setting_key: s.setting_key,
          setting_value: settingValues[s.id],
        },
        s.id,
      );
    });

    Promise.all(requests)
      .then(() => {
        toast("Settings updated successfully", ToastTypes.SUCCESS);
      })
      .catch(() => {
        toast("Failed to update settings", ToastTypes.ERROR);
      });
  };

  // Reset: toggles to false; printer type default; footer cleared
  const handleReset = () => {
    const resetValues: Record<number, string> = {};

    settings.forEach((s) => {
      if (s.id === undefined) return;
      const nk = normalizeKey(s.setting_key);
      if (nk === "printer type") {
        resetValues[s.id] = "laser";
      } else if (nk === "footer message") {
        resetValues[s.id] = "";
      } else {
        resetValues[s.id] = "false";
      }
    });

    setSettingValues(resetValues);

    toast("All settings reset — click Save to apply", ToastTypes.SUCCESS);
  };

  const renderControl = (setting: AppSettings) => {
    const id = setting.id;
    if (id === undefined) return null;

    const nk = normalizeKey(setting.setting_key);
    const raw = settingValues[id] ?? "";

    if (nk === "printer type") {
      const known = PRINTER_TYPE_OPTIONS.some((o) => o.value === raw);
      return (
        <select
          className="form-select form-select-sm"
          style={{ maxWidth: "14rem" }}
          id={`setting-printer-${id}`}
          value={raw}
          onChange={(e) => handleToggleSetting(id, e.target.value)}
        >
          {!known && raw ? <option value={raw}>{raw}</option> : null}
          {PRINTER_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
    }

    if (nk === "footer message") {
      return (
        <textarea
          className="form-control form-control-sm"
          id={`setting-footer-${id}`}
          rows={2}
          value={raw}
          placeholder="Footer text"
          onChange={(e) => handleToggleSetting(id, e.target.value)}
        />
      );
    }

    const isBool = raw === "true" || raw === "false";

    if (!isBool) {
      return (
        <input
          type="text"
          className="form-control form-control-sm"
          id={`setting-text-${id}`}
          value={raw}
          onChange={(e) => handleToggleSetting(id, e.target.value)}
        />
      );
    }

    return (
      <div className="d-flex align-items-center gap-3 ">
        <span
          className={`fw-semibold ${raw === "true" ? "text-success" : "text-danger"}`}
        >
          {raw === "true" ? "SHOW" : "HIDE"}
        </span>
        <div className="form-check form-switch m-0">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id={`setting-switch-${id}`}
            checked={raw === "true"}
            onChange={(e) =>
              handleToggleSetting(id, e.target.checked ? "true" : "false")
            }
            style={{ width: "2.5rem", height: "1.3rem" }}
          />
        </div>
      </div>
    );
  };

  const grouped = groupSettingsByGroup(settings);

  return (
    <UserLayout>
      <h3 className="mb-3">Application Settings</h3>

      <div className="container">
        {/* Buttons */}
        <div className="text-end my-2">
          <button className="btn btn-primary me-3" onClick={handleSaveAll}>
            Save All
          </button>
          <button className="btn btn-secondary" onClick={handleReset}>
            Reset All
          </button>
        </div>
        <div className="row">
          {grouped.map(({ groupLabel, items }) =>
            items.length > 1 ? (
              <div key={normalizeGroup(groupLabel)} className="col-12 mb-3">
                <div className="card shadow-sm border-0 rounded-3">
                  <div className="card-body">
                    <h5 className="fw-semibold mb-3 text-secondary border-bottom pb-2 text-capitalize">
                      {groupLabel}
                    </h5>
                    <div className="d-flex flex-column gap-3">
                      {items.map((setting) => (
                        <div
                          key={setting.id}
                          className="row align-items-center g-2"
                        >
                          <div className="col-12 col-md-4">
                            <label
                              className="form-label fw-semibold mb-0 text-capitalize"
                              htmlFor={
                                setting.id !== undefined
                                  ? `setting-control-${setting.id}`
                                  : undefined
                              }
                            >
                              {setting.setting_key}
                            </label>
                          </div>
                          <div
                            className="col-12 col-md-8"
                            id={
                              setting.id !== undefined
                                ? `setting-control-${setting.id}`
                                : undefined
                            }
                          >
                            {renderControl(setting)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              items.map((setting) => (
                <div
                  key={setting.id}
                  className="col-sm-4 col-md-8 col-lg-12 mb-3"
                >
                  <div className="card shadow-sm border-0 rounded-3">
                    <div className="card-body d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                      <div>
                        <h6 className="text-secondary text-capitalize mb-1">
                          {setting.setting_group}
                        </h6>
                        <h5 className="fw-semibold mb-0 text-capitalize">
                          {setting.setting_key}
                        </h5>
                      </div>
                      <div
                        className="w-100 align-self-stretch align-self-md-end"
                        style={{ maxWidth: "28rem" }}
                      >
                        {renderControl(setting)}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ),
          )}
        </div>
      </div>
    </UserLayout>
  );
};

export default AppSetting;
